// Local replacements for the ramda object helpers that have no short native
// form. Everything else ramda did here is written inline at the call site.
//
// Each of these keeps the ramda 0.28 behaviour the call sites relied on, and
// nothing more. These are not general-purpose ports. In particular none of
// them reads `equals` or `fantasy-land` methods, walks the prototype chain or
// handles cyclic values, because no state object in this app has those.

type Path = readonly (string | number)[]

const tag = (value: unknown): string => Object.prototype.toString.call(value)

const hasOwn = (obj: unknown, key: string | number): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key)

// Ramda checks the type tag, not the constructor, so a class instance counts
// as a plain object here as well.
const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  tag(value) === '[object Object]'

/**
 * Deep equality with the `R.equals` rules for primitives, arrays, plain
 * objects and dates: `NaN` equals `NaN`, `0` does not equal `-0`, object keys
 * are compared regardless of order, dates by time.
 *
 * Values of the same type tag compare by content, so a class instance equals a
 * plain object with the same keys, as in ramda. Any other type (`Map`, `Set`,
 * `RegExp`, boxed primitives) is equal only to itself, where ramda would
 * compare the contents.
 */
export const isDeepEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true

  const tagA = tag(a)
  if (tagA !== tag(b)) return false
  if (tagA === '[object Date]') {
    return Object.is((a as Date).valueOf(), (b as Date).valueOf())
  }
  if (tagA !== '[object Array]' && tagA !== '[object Object]') return false

  const left = a as Record<string, unknown>
  const right = b as Record<string, unknown>
  const keys = Object.keys(left)
  if (keys.length !== Object.keys(right).length) return false

  return keys.every(
    key => hasOwn(right, key) && isDeepEqual(left[key], right[key]),
  )
}

/**
 * `R.isEmpty`: true for `''`, `[]` and an object without own enumerable keys.
 * False for `null`, `undefined`, numbers, booleans, `Map`, `Set` and dates.
 */
export const isEmpty = (value: unknown): boolean => {
  if (typeof value === 'string' || Array.isArray(value))
    return value.length === 0
  return isPlainObject(value) && Object.keys(value).length === 0
}

/**
 * `R.mapObjIndexed`, and `R.map` on an object. Returns a new object with the
 * same keys in the same order. `null` and `undefined` give `{}`.
 */
export const mapValues = <T, U>(
  obj: Readonly<Record<string, T>> | null | undefined,
  fn: (value: T, key: string) => U,
): Record<string, U> => {
  const result: Record<string, U> = {}
  if (obj == null) return result
  for (const key of Object.keys(obj)) result[key] = fn(obj[key], key)
  return result
}

/**
 * `R.pickBy`, and `R.filter` on an object. Returns a new object with the kept
 * keys in their original order. `null` and `undefined` give `{}`.
 */
export const filterValues = <T>(
  obj: Readonly<Record<string, T>> | null | undefined,
  pred: (value: T, key: string) => boolean,
): Record<string, T> => {
  const result: Record<string, T> = {}
  if (obj == null) return result
  for (const key of Object.keys(obj)) {
    if (pred(obj[key], key)) result[key] = obj[key]
  }
  return result
}

const assoc = (obj: unknown, key: string | number, value: unknown): unknown => {
  if (Number.isInteger(key) && Array.isArray(obj)) {
    const copy = [...obj]
    copy[key as number] = value
    return copy
  }
  return { ...(obj as object), [key]: value }
}

const assocPath = (obj: unknown, path: Path, value: unknown): unknown => {
  if (path.length === 0) return value

  const [key, ...rest] = path
  if (rest.length === 0) return assoc(obj, key, value)

  // A missing level becomes an array when the next segment is an integer. A
  // level that exists but holds `null` is passed down as `null` and becomes an
  // object, which is what ramda does too.
  const next =
    obj != null && hasOwn(obj, key)
      ? (obj as Record<string, unknown>)[key]
      : Number.isInteger(rest[0])
        ? []
        : {}
  return assoc(obj, key, assocPath(next, rest, value))
}

const getPath = (obj: unknown, path: Path): unknown =>
  path.reduce<unknown>(
    (current, key) =>
      current == null ? undefined : (current as Record<string, unknown>)[key],
    obj,
  )

/**
 * `R.assocPath`, and `R.set` with `R.lensPath`. Copies every level along the
 * path, even when the value is unchanged, because selectors compare those
 * levels by reference. Missing levels are created as arrays for integer
 * segments and as objects otherwise.
 */
export const setPath = <T>(obj: T, path: Path, value: unknown): T =>
  assocPath(obj, path, value) as T

/**
 * `R.over` with `R.lensPath`. Calls `fn` with the current value, `undefined`
 * when the path is missing, then sets the result as `setPath` does. Negative
 * indexes are not read from the end of an array as `R.nth` would.
 *
 * `V` is taken from `fn` and is not checked against `obj`.
 */
export const updatePath = <T, V = unknown>(
  obj: T,
  path: Path,
  fn: (current: V) => unknown,
): T => setPath(obj, path, fn(getPath(obj, path) as V))

/**
 * `R.mergeDeepRight`. Plain objects are merged recursively and the right side
 * wins everywhere else: arrays are replaced, and `null` or `undefined` on the
 * right replaces an object on the left. Keys from the left come first.
 *
 * `null` or `undefined` as a whole argument is treated as `{}`. Ramda throws
 * on some of those combinations.
 */
export const mergeDeepRight = <A, B>(left: A, right: B): A & B => {
  const l = (left ?? {}) as Record<string, unknown>
  const r = (right ?? {}) as Record<string, unknown>
  const result: Record<string, unknown> = {}

  for (const key of Object.keys(l)) {
    if (!hasOwn(r, key)) result[key] = l[key]
    else if (isPlainObject(l[key]) && isPlainObject(r[key])) {
      result[key] = mergeDeepRight(l[key], r[key])
    } else result[key] = r[key]
  }
  for (const key of Object.keys(r)) {
    if (!hasOwn(result, key)) result[key] = r[key]
  }

  return result as A & B
}
